export type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'video'; url: string }
  | { type: 'callout'; text: string }

export interface Article {
  slug: string
  title: string
  excerpt: string
  img: string
  cat: string
  type: 'resource' | 'blog'
  date?: string
  readTime: string
  body: ContentBlock[]
}

export const articles: Article[] = [
  // --- RESOURCES ---
  {
    slug: 'dog-choking-response',
    title: 'What To Do If Your Dog Is Choking',
    excerpt:
      'Choking is a silent emergency. Learn the step-by-step response and modified Heimlich technique designed specifically for canine anatomy.',
    img: 'https://images.unsplash.com/photo-1537151608804-ea6f11840865?w=1200',
    cat: 'Emergency Response',
    type: 'resource',
    readTime: '5 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Choking is one of the most terrifying emergencies a pet parent can face. Unlike humans, dogs often choke silently, making it crucial to recognize the signs immediately and know exactly how to respond.',
      },
      { type: 'heading', text: 'Recognizing the Signs of Choking' },
      {
        type: 'paragraph',
        text: 'A choking dog will usually show signs of extreme distress. Look for these key indicators:',
      },
      {
        type: 'list',
        items: [
          'Pawing frantically at the mouth or face',
          'Gagging, retching, or making exaggerated swallowing motions',
          'Pale or blue-tinged gums (cyanosis) indicating lack of oxygen',
          'Uncharacteristic panic or pacing',
          'Loss of consciousness in severe cases',
        ],
      },
      {
        type: 'callout',
        text: 'WARNING: Do not confuse choking with a reverse sneeze or kennel cough. A choking dog is in acute distress and cannot breathe properly. If they are coughing loudly, air is still moving.',
      },
      { type: 'heading', text: 'Step 1: Check the Airway' },
      {
        type: 'paragraph',
        text: "If you suspect choking, carefully open your dog's mouth. Look inside for a visible obstruction. If you can clearly see the object and safely grasp it, try to remove it with your fingers or tweezers. However, be extremely careful not to push the object further down the throat.",
      },
      { type: 'video', url: 'https://www.youtube.com/embed/dQw4w9WgXcQ' }, // Placeholder video URL
      { type: 'heading', text: 'Step 2: The Canine Heimlich Maneuver' },
      {
        type: 'paragraph',
        text: 'If the airway is blocked and you cannot remove the object, you must perform the Heimlich maneuver modified for dogs.',
      },
      {
        type: 'list',
        items: [
          'For small dogs: Hold them with their back against your stomach. Place your fist in the soft hollow under their ribcage. Pull up and in firmly 4-5 times.',
          'For large dogs (standing): Stand behind them. Wrap your arms around their waist. Place your fist in the soft hollow just behind the ribs. Give firm upward and forward thrusts.',
          'For large dogs (lying down): Place one hand on their back for support. Use your other hand to squeeze the abdomen upwards and forwards towards the spine.',
        ],
      },
      {
        type: 'paragraph',
        text: 'After performing the Heimlich, check the mouth again to see if the object has been dislodged. Even if you successfully remove the object, always take your dog to the vet immediately for a follow-up examination, as internal damage may have occurred.',
      },
    ],
  },
  {
    slug: 'pet-cpr-basics',
    title: 'Pet CPR Basics Every Pet Owner Should Know',
    excerpt:
      'A clear, practical guide to performing chest compressions and rescue breathing on dogs and cats — the skills that can save a life before you reach the vet.',
    img: 'https://images.unsplash.com/photo-1544568100-847a948585b9?w=1200',
    cat: 'Core Skill',
    type: 'resource',
    readTime: '7 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Cardiopulmonary Resuscitation (CPR) for pets is a critical skill that can sustain life during cardiac arrest until veterinary care is available. While we hope you never have to use it, knowing the basics is essential.',
      },
      { type: 'heading', text: 'When to Perform CPR' },
      {
        type: 'paragraph',
        text: 'CPR should ONLY be performed if the pet is unconscious, not breathing, and has no heartbeat. Performing CPR on a healthy animal can cause serious injury.',
      },
      {
        type: 'callout',
        text: 'Check for breathing by watching the chest for movement or placing a mirror near the nose to look for condensation. Check for a heartbeat by placing your hand over the left side of the chest, just behind the elbow.',
      },
      { type: 'heading', text: 'The ABCs of Pet CPR' },
      {
        type: 'list',
        items: [
          'Airway: Ensure the airway is clear. Pull the tongue forward and check for obstructions.',
          "Breathing: Close the pet's mouth and breathe directly into their nose until you see the chest rise.",
          'Compressions: Perform chest compressions to circulate blood.',
        ],
      },
      { type: 'heading', text: 'Chest Compressions Technique' },
      {
        type: 'paragraph',
        text: 'The technique varies depending on the size and shape of the animal.',
      },
      {
        type: 'list',
        items: [
          'Small Dogs & Cats: Place one hand around the chest over the heart and squeeze.',
          'Deep-Chested Dogs (e.g., Greyhounds): Place hands directly over the heart on the widest part of the chest.',
          'Barrel-Chested Dogs (e.g., Bulldogs): Place the dog on its back and compress the sternum directly.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Aim for a rate of 100-120 compressions per minute. Alternate 30 compressions with 2 rescue breaths.',
      },
    ],
  },
  {
    slug: 'common-pet-emergencies',
    title: 'Common Pet Emergencies and How to Respond',
    excerpt:
      'From heatstroke to poisoning to seizures — learn how to recognize the most common pet emergencies and what to do in the critical first minutes.',
    img: 'https://images.unsplash.com/photo-1534361960057-19889db9621e?w=1200',
    cat: 'Awareness',
    type: 'resource',
    readTime: '6 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Being prepared for common emergencies can make a significant difference in the outcome for your pet. Here is a quick guide to recognizing and responding to frequent urgent situations.',
      },
      { type: 'heading', text: 'Heatstroke' },
      {
        type: 'paragraph',
        text: 'Heatstroke is a life-threatening condition caused by excessive heat exposure. Signs include heavy panting, drooling, red gums, vomiting, and collapse.',
      },
      {
        type: 'callout',
        text: 'Action: Move the pet to a cool area immediately. Apply cool (not ice-cold) water to their paws, belly, and armpits. Offer small amounts of water to drink. Seek veterinary care immediately.',
      },
      { type: 'heading', text: 'Poisoning' },
      {
        type: 'paragraph',
        text: 'Pets are curious and often ingest toxic substances like chocolate, grapes, certain plants, or household chemicals.',
      },
      {
        type: 'list',
        items: [
          'Identify the toxin if possible and estimate how much was ingested.',
          'Do NOT induce vomiting unless specifically instructed by a veterinarian or poison control center.',
          'Call your vet or the ASPCA Animal Poison Control Center immediately.',
        ],
      },
      { type: 'heading', text: 'Seizures' },
      {
        type: 'paragraph',
        text: "A seizure can look terrifying, but it's important to stay calm.",
      },
      {
        type: 'list',
        items: [
          'Do not try to restrain the pet or put anything in their mouth.',
          'Clear the area of hard or sharp objects to prevent injury.',
          'Time the seizure. If it lasts longer than 3-5 minutes, it is a critical emergency.',
          'Keep the environment quiet and dark as they recover.',
        ],
      },
    ],
  },
  {
    slug: '5-signs-emergency-care',
    title: '5 Signs Your Pet Needs Emergency Care Immediately',
    excerpt:
      'Learn how to distinguish between a minor upset stomach and a life-threatening emergency that requires immediate veterinary attention.',
    img: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=1200',
    cat: 'Awareness',
    type: 'resource',
    readTime: '4 min read',
    body: [
      {
        type: 'paragraph',
        text: 'It can be difficult to know when a symptom warrants an immediate trip to the emergency vet versus waiting for a regular appointment. Here are 5 red flags that should never be ignored.',
      },
      { type: 'heading', text: '1. Difficulty Breathing' },
      {
        type: 'paragraph',
        text: 'Any sign of respiratory distress is an absolute emergency. Look for open-mouthed breathing (in cats), exaggerated abdominal movement, pale or blue gums, or a posture with the neck extended and elbows out.',
      },
      { type: 'heading', text: '2. Unproductive Retching' },
      {
        type: 'paragraph',
        text: 'If your dog is trying to vomit but nothing is coming up, especially if accompanied by a distended abdomen and restlessness, it could be Gastric Dilatation-Volvulus (GDV or "bloat"). This is rapidly fatal without surgery.',
      },
      { type: 'heading', text: '3. Inability to Urinate' },
      {
        type: 'paragraph',
        text: 'Straining to urinate without producing urine is a hallmark sign of a urinary blockage, which is especially common and life-threatening in male cats. Toxins build up quickly and can cause cardiac arrest.',
      },
      { type: 'heading', text: '4. Severe Trauma or Bleeding' },
      {
        type: 'paragraph',
        text: 'Hit by a car, animal attacks, or falls from a height require immediate evaluation, even if the pet seems fine initially. Internal bleeding or injuries may not be immediately obvious.',
      },
      { type: 'heading', text: '5. Sudden Collapse or Profound Weakness' },
      {
        type: 'paragraph',
        text: 'If your pet suddenly cannot stand, collapses, or is extremely lethargic and unresponsive, seek immediate care. This can indicate internal bleeding, heart failure, or severe metabolic issues.',
      },
    ],
  },
  {
    slug: 'ultimate-first-aid-kit',
    title: 'Building the Ultimate Pet First Aid Kit',
    excerpt:
      'Whether you are at home or hitting the Bay Area trails, make sure you have these 10 essential first aid items ready for your pet.',
    img: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=1200',
    cat: 'Preparedness',
    type: 'resource',
    readTime: '5 min read',
    body: [
      {
        type: 'paragraph',
        text: 'A well-stocked first aid kit is your first line of defense in an emergency. While you can buy pre-made kits, building your own ensures you have high-quality items tailored to your pet.',
      },
      { type: 'heading', text: 'The Essentials' },
      {
        type: 'list',
        items: [
          'Gauze rolls and sterile gauze pads (for bandaging and controlling bleeding)',
          "Self-adhering bandage tape (like Vetrap) that won't stick to fur",
          'Blunt-tipped scissors (for cutting bandages or trimming hair around wounds)',
          'Tweezers (for removing ticks, splinters, or debris)',
          'Digital thermometer and petroleum jelly (for taking rectal temperature)',
          'Saline eye wash (for flushing eyes or wounds)',
          'Hydrogen peroxide 3% (ONLY to induce vomiting if directed by a vet)',
          'Disposable gloves',
          'A muzzle (even the sweetest pet may bite when in severe pain)',
          'Emergency contact card (Vet, Emergency Clinic, Poison Control)',
        ],
      },
      {
        type: 'callout',
        text: 'Tip: Keep one kit at home and a smaller version in your car or hiking backpack.',
      },
    ],
  },
  {
    slug: 'foxtails-hidden-danger',
    title: 'Foxtails: The Hidden Danger in California Grasses',
    excerpt:
      'A staple of the Bay Area landscape, foxtails can cause severe internal damage to dogs. How to spot them and safely remove them.',
    img: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=1200',
    cat: 'Local Hazards',
    type: 'resource',
    readTime: '4 min read',
    body: [
      {
        type: 'paragraph',
        text: 'If you live in California, you need to know about foxtails. These barbed seed heads from certain grasses are designed to burrow into the soil, but they will just as easily burrow into your dog.',
      },
      { type: 'heading', text: 'Why Foxtails Are Dangerous' },
      {
        type: 'paragraph',
        text: "Because of their backward-facing barbs, foxtails only move in one direction: forward. They can enter a dog's body through the nose, ears, eyes, paws, or even directly through the skin. Once inside, they can migrate through the body, causing severe infections, abscesses, and even death if they reach vital organs.",
      },
      { type: 'heading', text: 'Signs of a Foxtail Problem' },
      {
        type: 'list',
        items: [
          'Paws: Limping, excessive licking of a paw, swelling between toes.',
          'Ears: Frantic head shaking, pawing at the ear, redness, discharge.',
          'Nose: Sudden, violent sneezing, pawing at the nose, bloody discharge.',
          'Eyes: Squinting, tearing, redness, swelling.',
        ],
      },
      {
        type: 'callout',
        text: 'Prevention is key. Avoid walking in dry, tall grass during foxtail season (late spring through summer). Thoroughly brush and inspect your dog after every walk, paying special attention to the toes, ears, and armpits.',
      },
      {
        type: 'paragraph',
        text: 'If you suspect a foxtail is embedded and you cannot easily pull it out with tweezers, see a vet immediately. Do not try to dig it out, as it may break off and continue migrating.',
      },
    ],
  },

  // --- BLOG POSTS ---
  {
    slug: 'pettech-certification-journey',
    title: 'My PetTech Certification Journey',
    excerpt:
      'A look back at the intensive training, hands-on practice, and the moment I finally earned my PetTech instructor certification.',
    img: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200',
    cat: 'Certification',
    type: 'blog',
    date: 'Oct 12, 2023',
    readTime: '6 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Becoming a PetTech instructor was one of the most challenging and rewarding experiences of my career. After years working as a vet tech, I knew the medical side, but teaching it to pet parents required a whole new skill set.',
      },
      { type: 'heading', text: 'The Intensive Training' },
      {
        type: 'paragraph',
        text: "The program was rigorous. We spent days practicing on specialized canine and feline mannequins, running through high-stress scenarios. It wasn't just about knowing the steps; it was about building muscle memory and the ability to stay calm under pressure.",
      },
      {
        type: 'list',
        items: [
          'Mastering the modified Heimlich maneuver for different breeds',
          'Perfecting CPR compression rates and depths',
          'Learning how to effectively teach these skills to others',
        ],
      },
      {
        type: 'paragraph',
        text: 'The most impactful part was hearing stories from other instructors about the lives their students had saved. It reinforced my mission: education is the best preventative medicine.',
      },
    ],
  },
  {
    slug: 'behind-the-scenes-training',
    title: 'Behind the Scenes: A Day of Pet First Aid Training',
    excerpt:
      'Ever wonder what happens during an in-person workshop? Join me for a behind-the-scenes look at a recent group training session in San Jose.',
    img: 'https://images.unsplash.com/photo-1544568100-847a948585b9?w=1200',
    cat: 'Training',
    type: 'blog',
    date: 'Sep 28, 2023',
    readTime: '5 min read',
    body: [
      {
        type: 'paragraph',
        text: 'A typical Saturday morning starts early. I load up my car with "Casper" and "Fluffy" (my trusty CPR mannequins), boxes of bandages, and training manuals, heading to a local doggy daycare in San Jose.',
      },
      { type: 'heading', text: 'Hands-On Practice' },
      {
        type: 'paragraph',
        text: 'We always start with theory, but the real magic happens when people get their hands on the mannequins. There is always a moment of hesitation, followed by a sudden realization: "I can actually do this."',
      },
      {
        type: 'callout',
        text: "The most common feedback I get is how different it feels to perform compressions on a barrel-chested dog versus a deep-chested dog. You can't learn that from a book.",
      },
      {
        type: 'paragraph',
        text: 'By the end of the 4-hour session, the room is buzzing with confidence. They leave not just with a certificate, but with the peace of mind that they are prepared.',
      },
    ],
  },
  {
    slug: 'pet-professionals-cpr',
    title: 'Why Every Pet Professional Needs CPR Certification',
    excerpt:
      'Whether you are a dog walker, groomer, or pet sitter, having formal CPR and First Aid training is no longer just a nice-to-have—it is essential.',
    img: 'https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?w=1200',
    cat: 'Industry',
    type: 'blog',
    date: 'Sep 15, 2023',
    readTime: '4 min read',
    body: [
      {
        type: 'paragraph',
        text: "The pet care industry is booming, and pet parents are demanding higher standards of care. If you are entrusted with someone else's furry family member, you need to be prepared for the worst.",
      },
      { type: 'heading', text: 'Trust and Professionalism' },
      {
        type: 'paragraph',
        text: "Being certified sets you apart. It tells clients that you take your job seriously and that their pet's safety is your top priority. In an emergency, you are the first responder.",
      },
      {
        type: 'list',
        items: [
          'Dog walkers: Heatstroke and dog fights are real risks.',
          'Groomers: Stress-induced collapse or accidental cuts can happen.',
          'Pet sitters: You are the sole caretaker; you must know how to handle sudden illness.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Investing in a certification course is an investment in your business and the lives of the animals you care for.',
      },
    ],
  },
  {
    slug: '5-things-learned-instructor',
    title: '5 Things I Learned at My PetTech Instructor Course',
    excerpt:
      'The PetTech program goes beyond basic first aid. Here are the top five surprising things I learned during my instructor training.',
    img: 'https://images.unsplash.com/photo-1537151608804-ea6f11840865?w=1200',
    cat: 'Personal',
    type: 'blog',
    date: 'Aug 30, 2023',
    readTime: '5 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Even with a background in veterinary medicine, the PetTech instructor course opened my eyes to new perspectives on emergency response.',
      },
      { type: 'heading', text: 'Key Takeaways' },
      {
        type: 'list',
        items: [
          'The "Snout-To-Tail" Assessment is crucial for catching hidden injuries.',
          'Muzzling is almost always necessary in an emergency, even for the sweetest dogs.',
          "Improvisation is a vital skill when you don't have a full first aid kit.",
          'Human panic is often the biggest obstacle to saving a pet.',
          'Teaching muscle memory is more important than teaching theory.',
        ],
      },
    ],
  },
  {
    slug: 'bay-area-hazards',
    title: 'Bay Area Pet Safety: Local Hazards Every Owner Should Know',
    excerpt:
      'From toxic plants in the East Bay hills to specific wildlife encounters, here is what Bay Area pet owners need to watch out for.',
    img: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=1200',
    cat: 'Local',
    type: 'blog',
    date: 'Aug 14, 2023',
    readTime: '6 min read',
    body: [
      {
        type: 'paragraph',
        text: 'The Bay Area is a beautiful place to explore with your dog, but it comes with its own set of unique hazards.',
      },
      { type: 'heading', text: 'Wildlife and Plants' },
      {
        type: 'list',
        items: [
          'Rattlesnakes: Common in the hills during warmer months. Consider snake avoidance training.',
          'Coyotes: Keep small dogs close, especially at dawn and dusk.',
          'Toxic Algae: Avoid stagnant water in lakes and ponds during late summer.',
          "Poison Oak: While dogs don't usually react to it, the oils can transfer to you.",
        ],
      },
      {
        type: 'callout',
        text: 'Always carry plenty of water for both you and your dog, and know the location of the nearest emergency vet when hiking in remote areas.',
      },
    ],
  },
  {
    slug: 'vet-tech-to-instructor',
    title: 'From Vet Tech to Pet Safety Instructor: My Story',
    excerpt:
      'After 10 years in emergency veterinary clinics, I realized the biggest difference is made before the pet ever reaches the hospital.',
    img: 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=1200',
    cat: 'Personal',
    type: 'blog',
    date: 'Jul 22, 2023',
    readTime: '7 min read',
    body: [
      {
        type: 'paragraph',
        text: 'Working in the ER, I saw it all. The frantic owners rushing in with a choking dog, the heartbreaking cases of heatstroke. Too often, I thought, "If only they had known what to do 20 minutes ago."',
      },
      { type: 'heading', text: 'The Turning Point' },
      {
        type: 'paragraph',
        text: "The turning point for me was a case involving a golden retriever who had choked on a ball. The owner had tried to help but didn't know the proper technique. By the time they reached us, it was too late. That day, I decided I needed to focus on education.",
      },
      {
        type: 'paragraph',
        text: "I transitioned from treating emergencies to preventing them from becoming tragedies. Now, through PawVerse, I empower pet parents with the skills they need to be their pet's first responder.",
      },
    ],
  },
]
